// src/pages/offlineSales/CustomerPaymentsPage.jsx
//
// Customer-level receipts (payments not tied to a single invoice). Each receipt is allocated
// FIFO by the backend: opening balance first, then invoices oldest-first.
// Backend: rettzy-backend/src/modules/offlineSales/services/customerPayment.service.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HandCoins, Plus, User, Phone, XCircle, FileText, CalendarClock, Eye, Paperclip } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { getCustomerPayments, voidCustomerPayment } from '../../services/api/offlineSales/customerPayments';
import { getCustomers } from '../../services/api/offlineSales/customers';
import { getPaymentMethods } from '../../services/api/offlineSales/paymentMethods';
import { getImageUrl } from '../../utils/imageUtils';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate } from '../../utils/dateUtils';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './ReceivablesPage.module.css';
import chequeStyles from './ChequesPage.module.css';

const EMPTY_FILTERS = { status: '', customerId: '', paymentMethodId: '' };
const toCustomerOption = (c) => ({ value: c.id, label: c.phone ? `${c.name} (${c.phone})` : c.name });

const CustomerPaymentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get('highlight')) || null;
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('edit_offline_sales_order');

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0, pageSizeOptions: [20, 50, 100] });
  const [methods, setMethods] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [customerSearching, setCustomerSearching] = useState(false);

  useEffect(() => {
    getPaymentMethods()
      .then((data) => setMethods((Array.isArray(data) ? data : []).filter((m) => m.key !== 'credit_note_apply')))
      .catch(() => setMethods([]));
  }, []);

  const searchCustomers = useCallback(async (term = '') => {
    setCustomerSearching(true);
    try {
      const result = await getCustomers({ search: term.trim(), limit: 20, page: 1 });
      setCustomerOptions((result.customers || []).filter((c) => !c.isCashSales).map(toCustomerOption));
    } catch {
      setCustomerOptions([]);
    } finally {
      setCustomerSearching(false);
    }
  }, []);

  useEffect(() => { searchCustomers(''); }, [searchCustomers]);

  const mergedCustomerOptions = selectedCustomerOption && !customerOptions.some((o) => o.value === selectedCustomerOption.value)
    ? [selectedCustomerOption, ...customerOptions]
    : customerOptions;

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCustomerPayments({
        search: search.trim() || undefined,
        status: filters.status || undefined,
        customerId: filters.customerId || undefined,
        paymentMethodId: filters.paymentMethodId || undefined,
        page: pagination.currentPage,
        limit: pagination.pageSize,
      });
      setPayments(result.payments || []);
      setPagination((p) => ({ ...p, total: result.pagination?.total || 0 }));
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.cpFailedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [search, filters.status, filters.customerId, filters.paymentMethodId, pagination.currentPage, pagination.pageSize, showError, t]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const handleVoid = async (row) => {
    const confirmed = await confirm(t('offlineSales.cpVoidConfirm', { number: row.paymentNumber }), { type: 'danger' });
    if (!confirmed) return;
    try {
      await voidCustomerPayment(row.id);
      success(t('offlineSales.cpVoided'));
      loadPayments();
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.cpFailedToVoid'));
    }
  };

  const columns = [
    {
      key: 'paymentNumber',
      header: t('offlineSales.cpNumber'),
      render: (row) => (
        <div className={tb.partyInfo}>
          <div className={tb.partyName} style={row.id === highlightId ? { color: 'var(--color-primary)' } : undefined}>
            {row.paymentNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'paymentDate',
      header: t('offlineSales.cpPaymentDate'),
      render: (row) => <span className={tb.partyName}><CalendarClock size={14} />{formatDate(row.paymentDate)}</span>,
    },
    {
      key: 'customer',
      header: t('offlineSales.customer'),
      render: (row) => (
        <div className={tb.partyInfo}>
          <div className={tb.partyName}><User size={14} />{row.customer?.name}</div>
          {row.customer?.phone && <div className={tb.partyMeta}><Phone size={12} />{row.customer.phone}</div>}
        </div>
      ),
    },
    {
      key: 'method',
      header: t('offlineSales.cpMethod'),
      render: (row) => (
        <div className={tb.partyInfo}>
          <div>{row.PaymentMethod?.label}</div>
          {row.cheque && (
            <div className={tb.partyMeta}>
              <CalendarClock size={12} />
              {[formatDate(row.cheque.chequeDate), t(`offlineSales.chqStatus_${row.cheque.status}`)].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'reference',
      header: t('offlineSales.cpReference'),
      render: (row) => (row.reference ? <span>{row.reference}</span> : <span className={tb.naText}>—</span>),
    },
    {
      key: 'proof',
      header: t('offlineSales.cpProof'),
      render: (row) => (row.proofUrl ? (
        <a className={tb.docLink} href={getImageUrl(row.proofUrl)} target="_blank" rel="noopener noreferrer" title={t('offlineSales.cpViewProof')}>
          {row.proofUrl.toLowerCase().endsWith('.pdf') ? (
            <><Paperclip size={14} />{t('offlineSales.cpViewFile')}</>
          ) : (
            <img src={getImageUrl(row.proofUrl)} alt={t('offlineSales.cpProof')} className={styles.proofThumb} />
          )}
        </a>
      ) : <span className={tb.naText}>—</span>),
    },
    {
      key: 'amount',
      header: t('offlineSales.amount'),
      align: 'right',
      render: (row) => (
        <div className={tb.partyInfo}>
          <span className={tb.totalAmount}>{formatCurrency(row.amount)}</span>
          {parseFloat(row.unallocatedAmount) > 0 && (
            <span className={tb.partyMeta}>{t('offlineSales.cpUnallocated')}: {formatCurrency(row.unallocatedAmount)}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('offlineSales.statusCol'),
      render: (row) => (
        <span className={`${chequeStyles.statusBadge} ${row.status === 'void' ? chequeStyles.statusBounced : chequeStyles.statusCleared}`}>
          {t(`offlineSales.cpStatus_${row.status}`)}
        </span>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={tb.actionsWrapper}>
      <button
        className={`${tb.actionButton} ${tb.viewButton}`}
        onClick={() => navigate(`/offline-sales/customer-payments/${row.id}`)}
        title={t('offlineSales.cpView')}
      >
        <Eye size={14} />
      </button>
      <button
        className={`${tb.actionButton} ${tb.viewButton}`}
        onClick={() => navigate(`/offline-sales/customers/${row.customerId}/statement`)}
        title={t('offlineSales.customerStatement')}
      >
        <FileText size={14} />
      </button>
      {canWrite && row.status === 'active' && (
        <button
          className={`${tb.actionButton} ${tb.actionDanger}`}
          onClick={() => handleVoid(row)}
          title={t('offlineSales.cpVoid')}
        >
          <XCircle size={14} />
        </button>
      )}
    </div>
  );

  return (
    <div className={styles.receivablesPage}>
      <PageHeader
        title={t('offlineSales.cpTitle')}
        description={t('offlineSales.cpDescription')}
        icon={HandCoins}
        actions={canWrite && (
          <button className={styles.primaryBtn} onClick={() => navigate('/offline-sales/customer-payments/new')}>
            <Plus size={16} /> {t('offlineSales.cpReceivePayment')}
          </button>
        )}
      />

      <FilterPanel
        filters={[
          {
            key: 'customerId',
            type: 'custom',
            label: t('offlineSales.cpFilterCustomer'),
            render: (value, onChange) => (
              <Dropdown
                value={value ? Number(value) : null}
                onChange={(v) => {
                  const id = v ? Number(v) : null;
                  setSelectedCustomerOption(id ? mergedCustomerOptions.find((o) => o.value === id) || null : null);
                  onChange(id ? String(id) : '');
                }}
                options={mergedCustomerOptions}
                onSearch={searchCustomers}
                loading={customerSearching}
                placeholder={t('common.all', 'All')}
                searchPlaceholder={t('offlineSales.searchCustomers')}
              />
            ),
          },
          {
            key: 'paymentMethodId',
            type: 'select',
            label: t('offlineSales.cpFilterMethod'),
            options: [
              { value: '', label: t('common.all', 'All') },
              ...methods.map((m) => ({ value: String(m.id), label: m.label })),
            ],
          },
          {
            key: 'status',
            type: 'select',
            label: t('offlineSales.statusCol'),
            options: [
              { value: '', label: t('common.all', 'All') },
              { value: 'active', label: t('offlineSales.cpStatus_active') },
              { value: 'void', label: t('offlineSales.cpStatus_void') },
            ],
          },
        ]}
        values={filters}
        onChange={(next) => { setFilters({ ...EMPTY_FILTERS, ...next }); setPagination((p) => ({ ...p, currentPage: 1 })); }}
        onClear={() => { setFilters(EMPTY_FILTERS); setSelectedCustomerOption(null); setSearch(''); }}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: search,
          onChange: (value) => { setSearch(value); setPagination((p) => ({ ...p, currentPage: 1 })); },
          placeholder: t('offlineSales.cpSearchPlaceholder'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: (size) => setPagination((p) => ({ ...p, pageSize: size, currentPage: 1 })),
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={payments}
        columns={columns}
        actions={{ render: renderActions, width: '140px' }}
        loading={loading}
        pagination={pagination}
        onPaginationChange={(changes) => setPagination((p) => ({ ...p, ...changes }))}
        emptyMessage={t('offlineSales.cpEmpty')}
        emptyDescription={t('offlineSales.cpEmptyDescription')}
        emptyIcon={<HandCoins size={64} />}
      />
    </div>
  );
};

export default CustomerPaymentsPage;
